import { addDesigns, addItems, setSelected } from 'api/list';
import { logAddItemsToFavourites } from 'utils/analytics';
import { trackEvent } from 'utils/optimizely';
import { refreshCache } from 'utils/store';
import {
    FavouritesPayload,
    publish,
    ADD_FAILURE,
    ADD_ITEMS_FAILURE,
    ADD_ITEMS_LOADING,
    ADD_ITEMS_SUCCESS,
    ADD_LOADING,
    ADD_SUCCESS,
    isMultipleItemsPayload,
    isSingleItemPayload,
} from 'utils/pubsub';
import translate from 'utils/translate';
import { favouritesUrl } from 'utils/settings';
import { reportError } from 'utils/sentry';
import { getToastText } from './getToastText';

export const addToList = async ({
    payload,
    listId,
    listName,
    skipToast = true,
    isNewList = false,
}: {
    payload: FavouritesPayload;
    listId?: string;
    listName?: string;
    skipToast?: boolean;
    isNewList?: boolean;
    skipSavedForLaterToast?: boolean;
}): Promise<boolean> => {
    const formattedItemsPayload: Array<{
        itemNo: string;
        quantity: number;
        type: string;
    }> = isMultipleItemsPayload(payload)
        ? payload.items.map(({ productNumber, productType, quantity }) => ({
              itemNo: productNumber,
              quantity: Number(quantity),
              type: productType,
          }))
        : isSingleItemPayload(payload) && payload.design
        ? [
              {
                  itemNo: payload.design,
                  quantity: Number(payload.quantity),
                  type: 'planner',
              },
          ]
        : [
              {
                  itemNo: payload.productNumber,
                  quantity: Number(payload.quantity),
                  type: payload.productType,
              },
          ];

    const formattedItemsGraphqlPayload = formattedItemsPayload.map(
        ({ itemNo, quantity }) => ({
            itemNo,
            quantity,
        })
    );

    const formattedItemsGAPayload = [
        ...formattedItemsPayload,
        ...((isMultipleItemsPayload(payload) &&
            payload.designs?.map(design => ({
                itemNo: design.configurationId,
                quantity: Number(design.quantity),
                type: 'planner',
            }))) ||
            []),
    ];

    let itemsQuantity = formattedItemsGraphqlPayload.reduce(
        (sum, item) => sum + item.quantity,
        0
    );
    if (isMultipleItemsPayload(payload) && payload.designs) {
        const designsQuantity = payload.designs.reduce(
            (sum, design) => sum + design.quantity,
            0
        );
        itemsQuantity += designsQuantity;
    }

    if (isMultipleItemsPayload(payload)) {
        publish(ADD_ITEMS_LOADING, payload);
    } else {
        publish(ADD_LOADING, payload);
    }

    try {
        let res =
            isSingleItemPayload(payload) && payload.design
                ? await addDesigns(
                      {
                          designs: {
                              configurationId: payload.design,
                              quantity: Number(payload.quantity ?? 1),
                          },
                          listId: listId ?? null,
                      },
                      payload.clientId
                  )
                : await addItems(
                      {
                          items: formattedItemsGraphqlPayload,
                          listId: listId ?? null,
                      },
                      payload.clientId
                  );

        if (isNewList) {
            // Items added to new default list
            trackEvent('default_list_created');
        } else {
            // Items added to an existing list
            trackEvent('added_to_existing_list');
        }

        if (isMultipleItemsPayload(payload) && payload.designs) {
            res = await addDesigns(
                {
                    designs: payload?.designs,
                    listId: listId ?? null,
                },
                payload.clientId
            );
        }

        trackEvent('favourites_agent_list_add_one');
        if (listId) {
            await setSelected({ listId });
        }

        if (isMultipleItemsPayload(payload)) {
            publish(ADD_ITEMS_SUCCESS, { ...payload, skipToast });
        } else {
            publish(ADD_SUCCESS, { ...payload, skipToast });
        }

        const currentListId = listId ?? res.data.list.listId;

        window?.ikea?.toast(
            getToastText({
                payload,
                itemsQuantity,
                listName: listName || res.data.list.name,
            }),
            {
                label: translate('AGENT_TOAST_SHOW'),
                callbackOrHref: `${favouritesUrl}${currentListId}?view=${
                    payload.storeMode ? 'store' : 'retail'
                }`,
            },
            null
        );

        refreshCache();
        logAddItemsToFavourites({
            source: payload.source,
            listId: currentListId,
            isNewList,
            items: formattedItemsGAPayload,
            ga: payload?.ga,
        });
        return true;
    } catch (e) {
        reportError(e as Error);
        if (isMultipleItemsPayload(payload)) {
            publish(ADD_ITEMS_FAILURE, { ...payload, skipToast });
        } else {
            publish(ADD_FAILURE, { ...payload, skipToast });
        }

        window?.ikea?.toast(
            e === 'ILLEGAL_QUANTITY'
                ? translate('AGENT_CART_ILLEGALQUANTITY')
                : translate('GENERIC_ERROR_TOAST'),
            {
                label: translate('AGENT_TOAST_SHOW'),
                callbackOrHref: favouritesUrl,
            },
            null
        );
        return false;
    }
};
